import { Box, Button, Modal, Paper, Typography, Divider } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";

import { packs } from "../niveles/packs";
import { comprarServices } from "../../services/comprarServices";
import { useDispatch, useSelector } from "react-redux";
import { getCookie } from "../../utils/getCookies";
import { setInformacion } from "../../store/slices/usuarios";
import { rutas } from "../../rutas/rutas";
import { Alerta } from "../ux/Alert";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  boxShadow: 24,
  p: 0,
  width: "min(520px, 92vw)",
};

// ===== Config Pool =====
const POOL_BASE = "SWAP.HIVE";
const ENGINE_RPC = "https://enginerpc.com/contracts/";
const PRICE_HTTP_TIMEOUT_MS = 6000;
const DEPOSIT_POLL_MS = 3000;
const DEPOSIT_WAIT_MS = 150000;
const KEYCHAIN_WAIT_MS = 90000;
const ENGINE_CONFIRM_WAIT_MS = 35000;
const ENGINE_CONFIRM_POLL_MS = 2500;
const DEPOSIT_ACCOUNT = "cityfarm";

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function balanceForSymbol(userInfo, symbol) {
  if (!userInfo) return 0;
  if (symbol === "HIVE") return Number(userInfo?.hive || 0);
  if (symbol === "HUESO") return Number(userInfo?.hueso || 0);
  if (symbol === "VIPHUESO") return Number(userInfo?.viphueso || 0);
  return 0;
}

function engineSymbol(symbol) {
  return symbol === "HIVE" ? "SWAP.HIVE" : symbol;
}

async function fetchHiveEngineBalance(account, symbol) {
  if (!account || !symbol) return null;

  const res = await axios.post(
    ENGINE_RPC,
    {
      jsonrpc: "2.0",
      id: Date.now(),
      method: "findOne",
      params: {
        contract: "tokens",
        table: "balances",
        query: {
          account: String(account).trim().toLowerCase(),
          symbol: engineSymbol(symbol),
        },
      },
    },
    { timeout: PRICE_HTTP_TIMEOUT_MS }
  );

  const value = Number(res?.data?.result?.balance || 0);
  return Number.isFinite(value) ? value : 0;
}

function extractKeychainTxId(response) {
  const candidates = [
    response?.result?.id,
    response?.result?.tx_id,
    response?.result?.txid,
    response?.result?.transaction_id,
    response?.data?.id,
    response?.data?.tx_id,
    response?.data?.txid,
    typeof response?.result === "string" ? response.result : "",
  ];

  return String(candidates.find((v) => typeof v === "string" && v.length >= 20) || "");
}

function requestHiveEngineDeposit({ user, symbol, amount }) {
  return new Promise((resolve, reject) => {
    try {
      const keychain = typeof window !== "undefined" ? window.hive_keychain : null;
      if (!keychain) {
        reject(new Error("Hive Keychain is not installed or is not available in this browser."));
        return;
      }

      const quantity = Number(amount || 0);
      if (!Number.isFinite(quantity) || quantity <= 0) {
        reject(new Error("Invalid deposit amount."));
        return;
      }

      const token = engineSymbol(symbol);
      const amountText = quantity.toFixed(3);
      const memo = `Deposit ${token}`;
      let settled = false;

      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(
          new Error(
            "Hive Keychain did not return a result. No automatic purchase was made. Check Keychain and your Hive Engine history before trying again."
          )
        );
      }, KEYCHAIN_WAIT_MS);

      const finish = (response) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        console.log("CITYFARM_KEYCHAIN_DEPOSIT:", response);

        if (response?.success === true) {
          resolve({ response, txId: extractKeychainTxId(response) });
          return;
        }

        reject(
          new Error(
            response?.message ||
              response?.error ||
              "The Hive Keychain transfer was cancelled or failed."
          )
        );
      };

      // Keychain provides requestSendToken specifically for Hive Engine tokens.
      // It is safer here than building raw custom_json by hand.
      if (typeof keychain.requestSendToken === "function") {
        keychain.requestSendToken(
          user,
          DEPOSIT_ACCOUNT,
          amountText,
          memo,
          token,
          finish
        );
        return;
      }

      // Compatibility fallback for very old Keychain versions.
      if (typeof keychain.requestCustomJson === "function") {
        const json = {
          contractName: "tokens",
          contractAction: "transfer",
          contractPayload: {
            symbol: token,
            to: DEPOSIT_ACCOUNT,
            quantity: amountText,
            memo,
          },
        };

        keychain.requestCustomJson(
          user,
          "ssc-mainnet-hive",
          "Active",
          JSON.stringify(json),
          `Deposit ${amountText} ${token} to City Farm`,
          finish
        );
        return;
      }

      clearTimeout(timer);
      settled = true;
      reject(new Error("This Hive Keychain version cannot send Hive Engine tokens."));
    } catch (error) {
      reject(error);
    }
  });
}

async function waitForHiveEngineDebit({ account, symbol, beforeBalance, amount }) {
  if (beforeBalance === null || beforeBalance === undefined || !Number.isFinite(Number(beforeBalance))) {
    return { confirmed: null, balance: null };
  }

  const before = Number(beforeBalance);
  const expectedDrop = Number(amount || 0);
  const tolerance = Math.max(0.005, expectedDrop * 0.000001);
  const startedAt = Date.now();
  let lastBalance = before;

  while (Date.now() - startedAt < ENGINE_CONFIRM_WAIT_MS) {
    await sleep(ENGINE_CONFIRM_POLL_MS);

    try {
      const fresh = await fetchHiveEngineBalance(account, symbol);
      if (Number.isFinite(Number(fresh))) {
        lastBalance = Number(fresh);
        if (lastBalance <= before - expectedDrop + tolerance) {
          return { confirmed: true, balance: lastBalance };
        }
      }
    } catch (_) {
      // Temporary RPC error: keep waiting. Internal credit verification still protects the purchase.
    }
  }

  return { confirmed: false, balance: lastBalance };
}

export const PacksMarket = () => {
  const { informacion } = useSelector((state) => state.userSlice);
  const dispatch = useDispatch();

  const [item, setItem] = useState({});
  const [open3, setOpen3] = useState(false);

  const [criptomoneda, setCriptomoneda] = useState("VIPHUESO");

  // Precio token desde POOL (en HIVE/SWAP.HIVE)
  const [tokenPriceInHive, setTokenPriceInHive] = useState(0);

  // HIVE en USD
  const [hiveUsd, setHiveUsd] = useState(0);

  // Cantidad requerida en moneda seleccionada
  const [cantidad, setCantidad] = useState(0);

  const [error, setError] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [mensaje, setMensaje] = useState("");

  const [loadingPrices, setLoadingPrices] = useState(false);
  const [loadingBuy, setLoadingBuy] = useState(false);
  const [buyStage, setBuyStage] = useState("idle");
  const [walletBalance, setWalletBalance] = useState(null);
  const [loadingWalletBalance, setLoadingWalletBalance] = useState(false);
  const [pendingDeposit, setPendingDeposit] = useState(null);

  const handleCloseAlert = () => setOpenAlert(false);
  const handleClose3 = () => setOpen3(false);

  // ✅ Tu username real en BD
  const username = informacion?.nombre || "";

  // -------------------------
  // ✅ SALDO (SIEMPRE desde BD)
  // -------------------------
  const saldoDisponible = useMemo(() => {
    if (!informacion) return 0;

    if (criptomoneda === "HIVE") return Number(informacion?.hive || 0);
    if (criptomoneda === "HUESO") return Number(informacion?.hueso || 0);
    if (criptomoneda === "VIPHUESO") return Number(informacion?.viphueso || 0);

    return 0;
  }, [informacion, criptomoneda]);

  const saldoColor = useMemo(() => {
    if (!cantidad) return "white";
    return saldoDisponible >= Number(cantidad) ? "lime" : "red";
  }, [saldoDisponible, cantidad]);

  const canBuy = useMemo(() => {
    const req = Number(cantidad || 0);
    if (!username) return false;
    if (!req || req <= 0) return false;
    return true;
  }, [username, cantidad]);

  const needsDeposit = useMemo(() => {
    const req = Number(cantidad || 0);
    return req > 0 && saldoDisponible + 0.000001 < req;
  }, [saldoDisponible, cantidad]);


  const depositAmountNeeded = useMemo(() => {
    const req = Number(cantidad || 0);
    if (!req || saldoDisponible + 0.000001 >= req) return 0;

    const deficit = Math.max(0, req - saldoDisponible);
    const priceBuffer = Math.max(0.01, req * 0.01);
    return Math.ceil((deficit + priceBuffer) * 1000) / 1000;
  }, [cantidad, saldoDisponible]);

  const buyButtonLabel = useMemo(() => {
    if (!loadingBuy) {
      const hasPending =
        pendingDeposit?.symbol === criptomoneda &&
        Date.now() - Number(pendingDeposit?.sentAt || 0) < 30 * 60 * 1000;

      if (hasPending) return "CHECK PREVIOUS DEPOSIT";
      if (needsDeposit && depositAmountNeeded > 0) {
        return `DEPOSIT ${depositAmountNeeded.toFixed(3)} ${engineSymbol(criptomoneda)} & BUY`;
      }
      return "BUY NOW";
    }

    if (buyStage === "keychain") return "CONFIRM IN KEYCHAIN";
    if (buyStage === "chain") return "VERIFYING TRANSFER...";
    if (buyStage === "credit") return "WAITING FOR CITY FARM CREDIT...";
    if (buyStage === "buying") return "COMPLETING PURCHASE...";
    return "PREPARING...";
  }, [loadingBuy, buyStage, needsDeposit, depositAmountNeeded, criptomoneda, pendingDeposit]);

  useEffect(() => {
    if (!username || typeof window === "undefined") return;

    try {
      const raw = window.localStorage.getItem(`cityfarm:pending-deposit:${username}`);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      const age = Date.now() - Number(parsed?.sentAt || 0);
      if (age >= 0 && age < 30 * 60 * 1000) {
        setPendingDeposit(parsed);
      } else {
        window.localStorage.removeItem(`cityfarm:pending-deposit:${username}`);
      }
    } catch (_) {}
  }, [username]);

  useEffect(() => {
    if (!open3 || !username || !criptomoneda) {
      setWalletBalance(null);
      return undefined;
    }

    let cancelled = false;
    setLoadingWalletBalance(true);

    fetchHiveEngineBalance(username, criptomoneda)
      .then((value) => {
        if (!cancelled) setWalletBalance(value);
      })
      .catch(() => {
        if (!cancelled) setWalletBalance(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingWalletBalance(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open3, username, criptomoneda]);

  const rememberPendingDeposit = (value) => {
    setPendingDeposit(value);
    if (!username || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(`cityfarm:pending-deposit:${username}`, JSON.stringify(value));
    } catch (_) {}
  };

  const clearPendingDeposit = () => {
    setPendingDeposit(null);
    if (!username || typeof window === "undefined") return;
    try {
      window.localStorage.removeItem(`cityfarm:pending-deposit:${username}`);
    } catch (_) {}
  };

  // -------------------------
  // PRECIOS
  // -------------------------
  const fetchHiveUsd = async () => {
    const hiveRes = await axios.get(
      "https://api.coingecko.com/api/v3/simple/price?ids=hive&vs_currencies=usd",
      { timeout: PRICE_HTTP_TIMEOUT_MS }
    );
    return Number(hiveRes?.data?.hive?.usd || 0);
  };

  // Helper: convertir a número seguro
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  // Helper: extraer campos de reservas (muchos nodos/implementaciones usan nombres distintos)
  const extractReserves = (poolObj) => {
    // posibles nombres:
    // baseQuantity/quoteQuantity, baseBalance/quoteBalance, base/quote, token0/token1...
    const baseReserve =
      toNum(poolObj?.baseQuantity) ||
      toNum(poolObj?.baseBalance) ||
      toNum(poolObj?.base) ||
      0;

    const quoteReserve =
      toNum(poolObj?.quoteQuantity) ||
      toNum(poolObj?.quoteBalance) ||
      toNum(poolObj?.quote) ||
      0;

    return { baseReserve, quoteReserve };
  };

  // Helper: obtener símbolos del pool
  const extractSymbols = (poolObj, tokenPairStr) => {
    // comunes: baseSymbol / quoteSymbol
    const baseSymbol = poolObj?.baseSymbol || poolObj?.baseToken || poolObj?.tokenPair?.baseSymbol;
    const quoteSymbol = poolObj?.quoteSymbol || poolObj?.quoteToken || poolObj?.tokenPair?.quoteSymbol;

    if (baseSymbol && quoteSymbol) return { baseSymbol, quoteSymbol };

    // tokenPair "SWAP.HIVE:VIPHUESO"
    if (tokenPairStr && tokenPairStr.includes(":")) {
      const [a, b] = tokenPairStr.split(":");
      return { baseSymbol: a, quoteSymbol: b };
    }

    // a veces viene como tokenPair en el objeto
    const tp = poolObj?.tokenPair || poolObj?.pair || poolObj?.symbol;
    if (typeof tp === "string" && tp.includes(":")) {
      const [a, b] = tp.split(":");
      return { baseSymbol: a, quoteSymbol: b };
    }

    return { baseSymbol: "", quoteSymbol: "" };
  };

  // === Obtiene precio desde pool Diesel ===
  // Devuelve "precio del token (symbol) en SWAP.HIVE"
  const fetchTokenPriceInHiveFromPool = async (symbol) => {
    if (symbol === "HIVE") return 1;

    // Intentamos ambos órdenes de tokenPair
    const pairA = `${POOL_BASE}:${symbol}`;
    const pairB = `${symbol}:${POOL_BASE}`;

    const tryFindPool = async (tokenPair) => {
      // Contract/tabla más común en Diesel Pools: marketpools / pools
      const res = await axios.post(
        ENGINE_RPC,
        {
          jsonrpc: "2.0",
          id: Date.now(),
          method: "find",
          params: {
            contract: "marketpools",
            table: "pools",
            query: { tokenPair },
            limit: 1,
            offset: 0,
          },
        },
        { timeout: PRICE_HTTP_TIMEOUT_MS }
      );

      return res?.data?.result?.[0] || null;
    };

    let pool = null;
    let tokenPairUsed = "";

    try {
      pool = await tryFindPool(pairA);
      tokenPairUsed = pairA;
    } catch (_) {}

    if (!pool) {
      try {
        pool = await tryFindPool(pairB);
        tokenPairUsed = pairB;
      } catch (_) {}
    }

    // Si no encontramos pool, no hay precio real por liquidez
    if (!pool) return 0;

    const { baseReserve, quoteReserve } = extractReserves(pool);
    const { baseSymbol, quoteSymbol } = extractSymbols(pool, tokenPairUsed);

    if (!baseReserve || !quoteReserve) return 0;

    // Si baseSymbol es SWAP.HIVE y quoteSymbol es TOKEN:
    //   1 TOKEN = baseReserve/quoteReserve SWAP.HIVE
    // Si viene al revés:
    //   1 TOKEN = quoteReserve/baseReserve SWAP.HIVE
    if (baseSymbol === POOL_BASE && quoteSymbol === symbol) {
      return baseReserve / quoteReserve;
    }
    if (baseSymbol === symbol && quoteSymbol === POOL_BASE) {
      return quoteReserve / baseReserve;
    }

    // Fallback por si símbolos no vienen bien:
    // asumimos tokenPairUsed nos dice el orden
    if (tokenPairUsed === pairA) {
      // SWAP.HIVE:symbol => baseReserve(SWAP.HIVE) / quoteReserve(symbol)
      return baseReserve / quoteReserve;
    }
    if (tokenPairUsed === pairB) {
      // symbol:SWAP.HIVE => precio = quoteReserve(SWAP.HIVE) / baseReserve(symbol)
      return quoteReserve / baseReserve;
    }

    return 0;
  };

  // === Recalcular cantidad requerida ===
  const recalcularCantidad = (precioUsd, hiveUsdLocal, tokenPriceInHiveLocal, symbol) => {
    const usd = toNum(precioUsd);
    const hiveUsdNum = toNum(hiveUsdLocal);

    if (!usd || !hiveUsdNum) {
      setCantidad(0);
      return;
    }

    const totalHive = usd / hiveUsdNum; // USD -> HIVE

    if (symbol === "HIVE") {
      setCantidad(Number(totalHive.toFixed(3)));
      return;
    }

    const priceInHive = toNum(tokenPriceInHiveLocal);
    if (!priceInHive) {
      setCantidad(0);
      return;
    }

    // totalToken = HIVE / (HIVE por token)
    const totalToken = totalHive / priceInHive;
    setCantidad(Number(totalToken.toFixed(2)));
  };

  // -------------------------
  // OPEN MODAL
  // -------------------------
  const handleModal = async (pack) => {
    setItem(pack);

    // Mejora: decide moneda por pack.monedas (si existe), no por ids hardcodeados.
    // Regla:
    // - Si incluye HIVE => default HIVE
    // - Si no incluye HIVE y tiene varias => default primera
    // - Si tiene 1 => esa
    const monedas = Array.isArray(pack?.monedas) ? pack.monedas : [];
    let symbol = "HIVE";

    if (monedas.length === 1) {
      symbol = monedas[0];
    } else if (monedas.length > 1) {
      // si tiene HIVE, preferimos HIVE, si no, primera
      symbol = monedas.includes("HIVE") ? "HIVE" : monedas[0];
    }

    setCriptomoneda(symbol);
    setOpen3(true);
  };

  // Cuando se abre modal o cambia moneda, calculamos cantidad (con precios)
  useEffect(() => {
    const run = async () => {
      if (!open3) return;
      if (!item?.precio) return;

      try {
        setLoadingPrices(true);

        const hiveUsdLocal = await fetchHiveUsd();
        setHiveUsd(hiveUsdLocal);

        // Precio token en HIVE desde POOL
        const priceInHive = await fetchTokenPriceInHiveFromPool(criptomoneda);
        setTokenPriceInHive(priceInHive);

        recalcularCantidad(item?.precio, hiveUsdLocal, priceInHive, criptomoneda);

        // si no hay precio en pool, bloqueamos y avisamos
        if (criptomoneda !== "HIVE" && !priceInHive) {
          setError(true);
          setMensaje(`NO_LIQUIDITY_POOL_PRICE_FOR_${criptomoneda}`);
          setOpenAlert(true);
        }
      } catch (e) {
        setHiveUsd(0);
        setTokenPriceInHive(0);
        setCantidad(0);

        setError(true);
        setMensaje("PRICE_FETCH_ERROR");
        setOpenAlert(true);
      } finally {
        setLoadingPrices(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open3, criptomoneda, item?.id]);

  // -------------------------
  // REFRESH USER (después de comprar)
  // -------------------------
  const refreshUsuario = async () => {
    const tokenJWT = getCookie("token");
    if (!tokenJWT) return;

    try {
      const { data } = await axios.get(`${rutas.getUsuario}`, {
        headers: { token: tokenJWT },
        timeout: 10000,
      });
      dispatch(setInformacion(data));
      return data;
    } catch (_) {
      return null;
    }
  };

  const waitForDepositCredit = async (symbol, requiredAmount) => {
    const startedAt = Date.now();

    while (Date.now() - startedAt < DEPOSIT_WAIT_MS) {
      await sleep(DEPOSIT_POLL_MS);
      const freshUser = await refreshUsuario();
      const freshBalance = balanceForSymbol(freshUser, symbol);

      if (freshBalance + 0.01 >= Number(requiredAmount || 0)) {
        return freshUser;
      }
    }

    return null;
  };

  // -------------------------
  // COMPRAR (SIEMPRE valida saldo)
  // -------------------------
  const comprar = async () => {
    try {
      if (!username) {
        setError(true);
        setMensaje("NO_USERNAME");
        setOpenAlert(true);
        return;
      }

      const req = Number(cantidad || 0);
      if (!req || req <= 0) {
        setError(true);
        setMensaje("INVALID_AMOUNT");
        setOpenAlert(true);
        return;
      }

      if (criptomoneda !== "HIVE" && !tokenPriceInHive) {
        setError(true);
        setMensaje(`NO_POOL_PRICE_${criptomoneda}`);
        setOpenAlert(true);
        return;
      }

      setLoadingBuy(true);
      setBuyStage("preparing");

      let freshUser = await refreshUsuario();
      let currentInternalBalance = balanceForSymbol(freshUser || informacion, criptomoneda);

      if (currentInternalBalance + 0.01 < req) {
        if (username.includes("@")) {
          throw new Error("Associate a HIVE account before depositing with Keychain.");
        }

        // If a previous transfer was already confirmed on Hive Engine, NEVER send it twice.
        const pendingIsSameToken =
          pendingDeposit &&
          pendingDeposit.symbol === criptomoneda &&
          Date.now() - Number(pendingDeposit.sentAt || 0) < 30 * 60 * 1000;

        if (pendingIsSameToken) {
          setBuyStage("credit");
          setError(false);
          setMensaje(
            `A ${Number(pendingDeposit.amount || 0).toFixed(3)} ${engineSymbol(criptomoneda)} deposit was already confirmed. Checking City Farm credit; Keychain will NOT open again.`
          );
          setOpenAlert(true);

          const creditedUser = await waitForDepositCredit(criptomoneda, req);
          if (!creditedUser) {
            setError(false);
            setMensaje(
              "The blockchain transfer is already confirmed, but City Farm has not credited it yet. Do NOT deposit again. Wait a little and press Buy again to re-check the same deposit."
            );
            setOpenAlert(true);
            return;
          }

          clearPendingDeposit();
          freshUser = creditedUser;
          currentInternalBalance = balanceForSymbol(creditedUser, criptomoneda);
        }

        if (currentInternalBalance + 0.01 < req) {
          const deficit = Math.max(0, req - currentInternalBalance);
          const priceBuffer = Math.max(0.01, req * 0.01);
          const depositAmount = Math.ceil((deficit + priceBuffer) * 1000) / 1000;

          let beforeWallet = null;
          try {
            beforeWallet = await fetchHiveEngineBalance(username, criptomoneda);
            setWalletBalance(beforeWallet);
          } catch (_) {}

          if (beforeWallet !== null && beforeWallet !== undefined && Number.isFinite(Number(beforeWallet)) && Number(beforeWallet) + 0.0005 < depositAmount) {
            throw new Error(
              `Insufficient Hive Engine wallet balance. Available: ${Number(beforeWallet).toFixed(3)} ${engineSymbol(criptomoneda)}. Required deposit: ${depositAmount.toFixed(3)}.`
            );
          }

          setBuyStage("keychain");
          setError(false);
          setMensaje(
            `Confirm in Hive Keychain: send ${depositAmount.toFixed(3)} ${engineSymbol(criptomoneda)} to @${DEPOSIT_ACCOUNT}. The purchase will continue only after the transfer is verified.`
          );
          setOpenAlert(true);

          const keychainResult = await requestHiveEngineDeposit({
            user: username,
            symbol: criptomoneda,
            amount: depositAmount,
          });

          setBuyStage("chain");
          setMensaje("Keychain accepted the request. Verifying that the token really left your Hive Engine wallet...");
          setOpenAlert(true);

          const chainCheck = await waitForHiveEngineDebit({
            account: username,
            symbol: criptomoneda,
            beforeBalance: beforeWallet,
            amount: depositAmount,
          });

          if (chainCheck.confirmed === false) {
            throw new Error(
              "Keychain returned success, but the transfer has not appeared on Hive Engine yet. City Farm stopped here to avoid charging twice. Check your Keychain/Hive Engine history before retrying."
            );
          }

          if (Number.isFinite(Number(chainCheck.balance))) {
            setWalletBalance(Number(chainCheck.balance));
          }

          const pending = {
            symbol: criptomoneda,
            amount: depositAmount,
            txId: keychainResult?.txId || "",
            sentAt: Date.now(),
          };
          rememberPendingDeposit(pending);

          setBuyStage("credit");
          setMensaje(
            `Transfer confirmed. Waiting for City Farm to credit ${depositAmount.toFixed(3)} ${engineSymbol(criptomoneda)}. Do not send another deposit.`
          );
          setOpenAlert(true);

          const creditedUser = await waitForDepositCredit(criptomoneda, req);
          if (!creditedUser) {
            setError(false);
            setMensaje(
              "The transfer is confirmed on Hive Engine, but City Farm has not credited it yet. Do NOT deposit again. Wait a little and press Buy again; City Farm will re-check the same deposit without reopening Keychain."
            );
            setOpenAlert(true);
            return;
          }

          clearPendingDeposit();
        }
      } else if (pendingDeposit?.symbol === criptomoneda) {
        clearPendingDeposit();
      }

      setBuyStage("buying");
      await comprarServices(criptomoneda, item?.id);
      await refreshUsuario();

      setError(false);
      setMensaje("SUCCESS");
      setOpenAlert(true);
      handleClose3();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "ERROR";
      console.log("BUY_ERROR_DEBUG:", err?.response?.data || err);
      setError(true);
      setMensaje(msg);
      setOpenAlert(true);
    } finally {
      setLoadingBuy(false);
      setBuyStage("idle");
    }
  };

  return (
    <Box className="rpgui-container framed-golden">
      <Grid container spacing={2} sx={{ p: 1 }}>
        {packs.map((p) => {
          const comprado = informacion?.pack?.[p.id] === true;

          return (
            <Grid key={uuidv4()} xs={12} sm={6} md={4} lg={3}>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  background: "rgba(0,0,0,0.10)",
                  borderRadius: 2,
                  filter: comprado ? "grayscale(1)" : "grayscale(0)",
                }}
                className="rpgui-container framed-golden"
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Typography sx={{ fontWeight: 800 }}>{p?.nombre}</Typography>
                  {comprado && <Typography sx={{ fontSize: 12, color: "gold" }}>OWNED</Typography>}
                </Box>

                <Divider sx={{ opacity: 0.25 }} />

                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 12, opacity: 0.9, mb: 0.5 }}>Items</Typography>

                  {p?.inventario?.map((e) => (
                    <Box key={uuidv4()} sx={{ mb: 0.5 }}>
                      {Object.entries(e).map(([k, v]) => (
                        <Typography key={uuidv4()} sx={{ fontSize: 13, opacity: 0.95 }}>
                          {k}: <b>{v}</b>
                        </Typography>
                      ))}
                    </Box>
                  ))}

                  {!!p?.tierras?.length && (
                    <>
                      <Typography sx={{ fontSize: 12, opacity: 0.9, mt: 1 }}>Lands</Typography>
                      {p.tierras.map((t) => (
                        <Typography key={uuidv4()} sx={{ fontSize: 13, color: "lime" }}>
                          {t.nombre}
                        </Typography>
                      ))}
                    </>
                  )}
                </Box>

                <Divider sx={{ opacity: 0.25 }} />

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <Typography sx={{ fontSize: 14 }}>
                    <b>{p?.precio}</b> USD
                  </Typography>
                  <Typography sx={{ fontSize: 12, opacity: 0.8 }}>
                    {JSON.stringify(p?.monedas)}
                  </Typography>
                </Box>

                <Button disabled={comprado} onClick={() => handleModal(p)} sx={{ mt: 1 }} class="rpgui-button">
                  <p>{comprado ? "Owned" : "Buy"}</p>
                </Button>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* MODAL */}
      <Modal open={open3} onClose={handleClose3} sx={{ position: "absolute" }}>
        <Box sx={modalStyle}>
          <Paper
            elevation={6}
            className="rpgui-container framed-golden"
            sx={{ p: 2, borderRadius: 2, background: "rgba(0,0,0,0.20)" }}
          >
            <Typography sx={{ fontWeight: 900, mb: 1 }}>Checkout</Typography>
            <Divider sx={{ opacity: 0.25, mb: 1 }} />

            {/* Selector basado en pack.monedas (si tiene varias) */}
            {Array.isArray(item?.monedas) && item.monedas.length > 1 && (
              <Box sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 12, opacity: 0.9, mb: 0.5 }}>Choose currency</Typography>
                <select
                  style={{ width: "100%", height: 38 }}
                  value={criptomoneda}
                  onChange={(e) => setCriptomoneda(e.target.value)}
                  class="rpgui-dropdown"
                >
                  {item.monedas.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </Box>
            )}

            <Box sx={{ display: "grid", gap: 0.5 }}>
              <Typography>Pack: <b>{item?.nombre}</b></Typography>
              <Typography>Price (USD): <b>{item?.precio}</b></Typography>

              <Typography sx={{ opacity: 0.95 }}>
                HIVE price (USD): <b>{loadingPrices ? "..." : hiveUsd}</b>
              </Typography>

              {criptomoneda !== "HIVE" && (
                <Typography sx={{ opacity: 0.95 }}>
                  {criptomoneda} price (from POOL, in {POOL_BASE}):{" "}
                  <b>{loadingPrices ? "..." : tokenPriceInHive}</b>
                </Typography>
              )}

              <Divider sx={{ opacity: 0.25, my: 1 }} />

              <Typography>
                Amount required ({criptomoneda}): <b>{loadingPrices ? "..." : cantidad}</b>
              </Typography>

              <Typography sx={{ color: saldoColor }}>
                Your balance ({criptomoneda}): <b>{saldoDisponible}</b>
              </Typography>


              {needsDeposit && (
                <Box
                  sx={{
                    mt: 0.75,
                    p: 1,
                    border: "1px solid rgba(255,193,7,0.55)",
                    borderRadius: 1,
                    background: "rgba(255,193,7,0.08)",
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#ffd56a" }}>
                    Deposit required: {depositAmountNeeded.toFixed(3)} {engineSymbol(criptomoneda)}
                  </Typography>
                  <Typography sx={{ fontSize: 12, mt: 0.35, color: "white" }}>
                    Hive Engine wallet: {loadingWalletBalance ? "checking..." : walletBalance === null ? "unavailable" : Number(walletBalance).toFixed(3)} {engineSymbol(criptomoneda)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, mt: 0.35, opacity: 0.9 }}>
                    Receiver: @{DEPOSIT_ACCOUNT}. Keychain opens only when you press the deposit button below.
                  </Typography>
                </Box>
              )}

              {pendingDeposit?.symbol === criptomoneda &&
                Date.now() - Number(pendingDeposit?.sentAt || 0) < 30 * 60 * 1000 && (
                  <Typography sx={{ fontSize: 12, color: "#ffe082", fontWeight: 800 }}>
                    A previous {Number(pendingDeposit.amount || 0).toFixed(3)} {engineSymbol(criptomoneda)} deposit is already pending credit. Press CHECK PREVIOUS DEPOSIT. Keychain will not open again.
                  </Typography>
                )}

              {needsDeposit && !loadingPrices && (
                <Typography sx={{ fontSize: 12, color: "orange" }}>
                  Your City Farm balance is not enough. The button below shows the exact deposit amount. After Keychain confirmation, City Farm verifies the blockchain transfer before continuing.
                </Typography>
              )}

              {criptomoneda !== "HIVE" && !loadingPrices && !tokenPriceInHive && (
                <Typography sx={{ fontSize: 12, color: "orange" }}>
                  No pool price available for {criptomoneda}. Make sure there is a Diesel Pool {POOL_BASE}:{criptomoneda} with liquidity.
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              <Button onClick={handleClose3} sx={{ flex: 1 }} class="rpgui-button">
                <p>Cancel</p>
              </Button>

              <Button
                onClick={loadingBuy ? undefined : comprar}
                sx={{
                  flex: 1.35,
                  minHeight: 58,
                  px: 1.25,
                  pointerEvents: loadingBuy ? "none" : "auto",
                  opacity: "1 !important",
                  "&.Mui-disabled": {
                    opacity: "1 !important",
                  },
                }}
                className="rpgui-button"
                disabled={!canBuy || loadingPrices || (criptomoneda !== "HIVE" && !tokenPriceInHive)}
                aria-busy={loadingBuy ? "true" : "false"}
              >
                <span
                  style={{
                    display: "block",
                    width: "100%",
                    color: "#241407",
                    fontWeight: 900,
                    fontSize: 13,
                    lineHeight: 1.25,
                    textAlign: "center",
                    textShadow: "none",
                    opacity: 1,
                    whiteSpace: "normal",
                  }}
                >
                  {buyButtonLabel}
                </span>
              </Button>
            </Box>

            {!username && (
              <Typography sx={{ mt: 1, fontSize: 12, color: "red" }}>
                No username found (expected: informacion.nombre).
              </Typography>
            )}
          </Paper>
        </Box>
      </Modal>

      <Alerta mensaje={mensaje} open={openAlert} handleClose={handleCloseAlert} error={error} />
    </Box>
  );
};
